/**
 * Junto Frontend AuthPopup Component
 * 
 * This is the modular authentication popup that can be used by any studio.
 * It maintains exact visual equivalence to the Sol Room auth popup while
 * being configurable for other studios through theme props.
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal, Button, Input, TabGroup, Tab } from '../ui/index.js';
import { solRoomTheme } from '../../themes/sol-room.js';

const FormContainer = styled.div`
  position: relative;
  height: ${props => props.$height}px;
  overflow: hidden;
  transition: height 0.2s ease; /* Reduced from 0.3s for snappier response */
  will-change: height; /* Optimize height animations */
`;

const MeasuringForm = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  visibility: hidden;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  width: 400px; /* Same as popup width */
`;

const Form = styled(motion.form)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  width: 100%;
  will-change: transform, opacity; /* Optimize form animations */
  transform: translateZ(0); /* Force GPU layer */
`;

const NameRow = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  width: 100%;
  overflow: hidden;
  
  > * {
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || solRoomTheme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
    
    > * {
      flex: none;
    }
  }
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
`;

const SwitchLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  font-size: 0.85rem;
  text-align: center;
  padding: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  background: ${props => props.theme?.colors?.accent?.bright || solRoomTheme.colors.accent.bright};
  border-radius: 4px;
`;

const AuthPopup = ({ 
  isOpen, 
  onClose, 
  onLogin, 
  onSignup, 
  isLoading = false,
  theme = solRoomTheme 
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [previousIsLogin, setPreviousIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [containerHeight, setContainerHeight] = useState(300);
  const loginMeasureRef = useRef(null);
  const signupMeasureRef = useRef(null);

  // Measure both forms and set container height to max
  useEffect(() => {
    const measureHeight = () => {
      if (loginMeasureRef.current && signupMeasureRef.current) {
        const loginHeight = loginMeasureRef.current.offsetHeight;
        const signupHeight = signupMeasureRef.current.offsetHeight;
        const maxHeight = Math.max(loginHeight, signupHeight);
        setContainerHeight(maxHeight + 20); // Add small buffer
      }
    };

    // Add a small delay to ensure forms are rendered
    const timer = setTimeout(measureHeight, 50);
    return () => clearTimeout(timer);
  }, [formData, error, isOpen]); // Also recalculate when popup opens

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (!isLogin && (!formData.firstName || !formData.lastName || !formData.phone)) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      let result;
      if (isLogin) {
        result = await onLogin(formData.email, formData.password);
      } else {
        const userData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone
        };
        result = await onSignup(userData);
      }

      if (result?.success) {
        // Reset form and close popup
        setFormData({ email: '', password: '', firstName: '', lastName: '', phone: '' });
        onClose();
      } else {
        setError(result?.error || 'Authentication failed');
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      // Handle specific API errors
      if (err.message.includes('422')) {
        setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      } else if (err.message.includes('401')) {
        setError('Invalid email or password');
      } else if (err.message.includes('400')) {
        setError('Please check your input and try again');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    }
  };

  const handleTabSwitch = (loginMode) => {
    setPreviousIsLogin(isLogin);
    setIsLogin(loginMode);
    setError('');
  };

  // Render form content based on login state
  const renderFormContent = (isLoginForm) => (
    <>
      {!isLoginForm && (
        <>
          <NameRow theme={theme}>
            <Input
              label="First Name"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter your first name"
              required
              theme={theme}
            />
            
            <Input
              label="Last Name"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter your last name"
              required
              theme={theme}
            />
          </NameRow>
          
          <Input
            label="Phone Number"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter your phone number"
            required
            theme={theme}
          />
        </>
      )}
      
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        placeholder="Enter your email"
        required
        theme={theme}
      />
      
      <Input
        label="Password"
        type="password"
        value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)}
        placeholder="Enter your password"
        required
        hint={!isLoginForm ? 'Must contain at least one uppercase letter, one lowercase letter, and one number' : undefined}
        theme={theme}
      />

      {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
      
      <Button
        type="submit"
        variant="filled"
        size="medium"
        fullWidth
        loading={isLoading}
        disabled={isLoading}
        theme={theme}
      >
        {isLoginForm ? 'Login' : 'Sign Up'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="400px" theme={theme}>
      <TabGroup theme={theme}>
        <Tab 
          active={isLogin} 
          onClick={() => handleTabSwitch(true)}
          theme={theme}
        >
          Login
        </Tab>
        <Tab 
          active={!isLogin} 
          onClick={() => handleTabSwitch(false)}
          theme={theme}
        >
          Sign Up
        </Tab>
      </TabGroup>

      {/* Hidden measuring forms */}
      <MeasuringForm ref={loginMeasureRef} theme={theme}>
        {renderFormContent(true)}
      </MeasuringForm>
      <MeasuringForm ref={signupMeasureRef} theme={theme}>
        {renderFormContent(false)}
      </MeasuringForm>

      <FormContainer $height={containerHeight}>
        <AnimatePresence>
          <Form 
            key={isLogin ? 'login' : 'signup'}
            onSubmit={handleSubmit}
            style={{ position: 'absolute', width: '100%' }}
            initial={{ 
              x: isLogin && !previousIsLogin ? -50 : // Reduced from -300 for smoother animation
                 !isLogin && previousIsLogin ? 50 : // Reduced from 300
                 0,
              opacity: 0 
            }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ 
              x: isLogin ? -50 : 50, // Reduced exit distance
              opacity: 0 
            }}
            transition={{ duration: 0.2, ease: "easeOut" }} // Faster and smoother
            theme={theme}
          >
            {renderFormContent(isLogin)}
          </Form>
        </AnimatePresence>
      </FormContainer>

      <SwitchText theme={theme}>
        <AnimatePresence mode="wait">
          <motion.span
            key={isLogin ? 'loginText' : 'signupText'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <SwitchLink 
              onClick={() => handleTabSwitch(!isLogin)}
              type="button"
              theme={theme}
            >
              {isLogin ? 'Sign up here' : 'Login here'}
            </SwitchLink>
          </motion.span>
        </AnimatePresence>
      </SwitchText>
    </Modal>
  );
};

export default AuthPopup;